import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import endpointsConfig from "endpoints.config";

class SignalRService {
  private connection: HubConnection | null = null;
  private readonly hubUrl: string = `${endpointsConfig.UserBaseUrl}residentialgatewayhub`;
  private readonly handlerMap = new Map<
    string,
    Map<Function, (...args: any[]) => void>
  >();

  async startConnection(): Promise<void> {
    if (!this.connection) {
      this.connection = new HubConnectionBuilder()
        .withUrl(this.hubUrl)
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      this.connection.onclose((error) => {
        console.error("SignalR connection closed:", error);
        this.connection = null;
        this.handlerMap.clear();
        setTimeout(() => this.startConnection(), 5000);
      });
      this.connection.onreconnecting((error) => {
        console.warn("Reconnecting...", error);
      });

      try {
        await this.connection.start();
        console.info("SignalR connected.");
      } catch (err) {
        console.error("SignalR connection error:", err);
        setTimeout(() => this.startConnection(), 5000); // Retry after 5 seconds
      }
    }
  }

  onReconnected(callback: (connectionId: string | undefined) => void): void {
    if (this.connection) {
      this.connection.onreconnected((connectionId) => {
        console.info(" SignalR reconnected. Connection ID:", connectionId);
        callback(connectionId); // <-- your custom logic goes here
      });
    }
  }
  stopConnection(): void {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
      this.handlerMap.clear();
      console.info("SignalR connection stopped.");
    }
  }

  on<T>(methodName: string, callback: (data: T) => void): void {
    if (this.connection) {
      const wrapper = (data: T) => {
        try {
          const parsedData = typeof data === "string" ? JSON.parse(data) : data;
          callback(parsedData);
        } catch (error) {
          console.error("Error parsing received SignalR data:", error);
        }
      };
      if (!this.handlerMap.has(methodName)) {
        this.handlerMap.set(methodName, new Map());
      }
      this.handlerMap.get(methodName)!.set(callback, wrapper);
      this.connection.on(methodName, wrapper);
    } else {
      console.warn(
        `Connection not initialized. Cannot subscribe to method: ${methodName}`,
      );
    }
  }

  off(methodName: string, callback?: Function): void {
    if (this.connection) {
      if (callback) {
        const wrapper = this.handlerMap.get(methodName)?.get(callback);
        if (wrapper) {
          this.connection.off(methodName, wrapper);
          this.handlerMap.get(methodName)!.delete(callback);
        }
      } else {
        this.connection.off(methodName);
        this.handlerMap.delete(methodName);
      }
    } else {
      console.warn(
        `Connection not initialized. Cannot unsubscribe from method: ${methodName}`,
      );
    }
  }

  async invoke<T>(methodName: string, ...args: any[]): Promise<T | void> {
    if (this.connection) {
      try {
        return await this.connection.invoke<T>(methodName, ...args);
      } catch (error) {
        console.error(`Error invoking method ${methodName}:`, error);
      }
    } else {
      console.warn(
        `Connection not initialized. Cannot invoke method: ${methodName}`,
      );
    }
  }
}

export const signalRService = new SignalRService();
